/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {ButtonProps} from '@material-ui/core';
import {GenericObject} from '@ion/core/src/types/entities';
import {withDLSTheme} from '../../theme/withDLSTheme';
import {
  FloatingIconButton as CurrentFloatingIconButton,
  FloatingIconButtonGroup as CurrentFloatingIconButtonGroup
} from './current/FloatingIconButton';
import {
  FloatingIconButton as LegacyFloatingIconButton,
  FloatingIconButtonGroup as LegacyFloatingIconButtonGroup
} from './legacy/FloatingIconButton';

export interface FloatingIconButtonProps extends ButtonProps {
  children: JSX.Element;
  label?: string;
  tooltipLabel?: string;
  isGrouped?: boolean;
  /**
   * If `true`, Button will be in selected mode.
   */
  isSelected?: boolean;
  round?: boolean;
  backgroundColor?: string;
  shimmer?: boolean;
}

export interface FloatingIconButtonGroupProps {
  /**
   * Content of the `FloatingIconButtonGroup`.
   */
  children: JSX.Element;
  /**
   * If `true`, Align the icons in vertical.
   *
   * By default, Icons are aligned in horizontal.
   */
  isVertical?: boolean;
  /**
   * If true, the icon will be displayed without a shadow and background.
   */
  noShadow?: boolean;
  style?: GenericObject;
}

type FloatingIconButtonGroupComponent = React.FC<FloatingIconButtonGroupProps> & {
  FloatingIconButton: React.ComponentType<FloatingIconButtonProps>;
};

const FloatingIconButton = withDLSTheme(LegacyFloatingIconButton, CurrentFloatingIconButton);
const ThemedFloatingIconButtonGroup = withDLSTheme(LegacyFloatingIconButtonGroup, CurrentFloatingIconButtonGroup);

/**
 * Floating icon button group used for map/report tool actions.
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
 *
 * const Example = () => (
 *   <FloatingIconButtonGroup isVertical>
 *     <>
 *       <FloatingIconButtonGroup.FloatingIconButton onClick={() => {}}>
 *         <MapZoomInIcon size={IconSize.large} />
 *       </FloatingIconButtonGroup.FloatingIconButton>
 *       <FloatingIconButtonGroup.FloatingIconButton onClick={() => {}}>
 *         <MapZoomOutIcon size={IconSize.large} />
 *       </FloatingIconButtonGroup.FloatingIconButton>
 *     </>
 *   </FloatingIconButtonGroup>
 * );
 * ```
 */
const FloatingIconButtonGroup = ThemedFloatingIconButtonGroup as FloatingIconButtonGroupComponent;

FloatingIconButtonGroup.FloatingIconButton = FloatingIconButton;

export {FloatingIconButtonGroup};
